import React from 'react';
import { parseTerms, replaceURLWithHTMLLinks } from '../../common/common';
import { useCartData } from '../../hooks/order';
import { generalTermsStatic, spanishGeneralTermsStatic, vasProductTermsSpanish } from '../../util/util';
import InternetTermsSection from './InternetTermsSection';

// Every terms section (Internet, Mobile, add-ons, General) without the scroll container, so the
// single-page checkout can drop it into its own scroll box and show the same full terms the
// legacy Terms & Conditions step shows.
export const TermsContent = () => {
  const {
    isMobileAdded,
    vasPlan,
    spanishLanguagePref,
    generalTerms,
  } = useCartData();

  const generalTermsSpanishArr = parseTerms(generalTerms?.[0]?.termsOfSrvcEsp);

  return (
    <>
      <InternetTermsSection />
      {isMobileAdded && (
        <div className="m-bottom-0-5">
          {!spanishLanguagePref ? (
            <>
              <h4>Mobile Service</h4>
              <ul>
                <li>
                  EarthLink Mobile requires activation. SIM cards will be
                  cancelled if not activated within 28 days of purchase.
                </li>
                <li>
                  Your 4G LTE and 5G speeds require a compatible device with
                  our network. If data limits are exceeded, speeds are reduced
                  to 128 kbps until reset.
                </li>
                <li>
                  Your EarthLink Mobile Service is powered by Ultra Mobile,
                  speeds and coverage are not available in all areas, and
                  International long-distance is provided by PLD, LLC, a
                  company not affiliated with EarthLink Mobile.
                </li>
              </ul>
            </>
          ) : (
            <>
              <h4>Productos Móviles</h4>
              <ul>
                <li>
                  El servicio de EarthLink Móvil requiere activación. Las
                  tarjetas SIM se cancelarán si no son activadas en 28 días de
                  haberlos comprado.
                </li>
                <li>
                  Las velocidades de 4G LTE y 5G requieren un dispositivo que
                  sea compatible con nuestra red. Si usted excede los límites
                  de datos, sus velocidades serán reducidas a 128 kbps hasta
                  que se reinicia su servicio el próximo ciclo mensual.
                </li>
                <li>
                  Sus Servicio de EarthLink Móvil está impulsada por Ultra
                  Mobile, y las velocidades y la cobertura no están
                  disponibles en todas las áreas. El servicio internacional
                  está impulsada por PLD, LLC, una compañía que no está
                  afiliada con EarthLink Móvil.
                </li>
              </ul>
            </>
          )}
        </div>
      )}
      {vasPlan && vasPlan.length > 0 && (
        <div id="termsAccordion">
          <div>
            {vasPlan.map((vasProd, i) => (
              <div key={i}>
                {vasProd.termsOfSrvc ? (
                  <>
                    {!spanishLanguagePref ? (
                      <h4>{vasProd.serviceName.replace('EarthLink', '')}</h4>
                    ) : (
                      vasProductTermsSpanish.map((terms) => {
                        if (
                          vasProd.serviceName === terms.heading &&
                        terms.serviceTerm !== ''
                        ) {
                          return (
                            <h4 key={terms.heading}>{vasProd.serviceName.replace('EarthLink', '')}</h4>
                          );
                        }
                      })
                    )}
                    <div>
                      {!spanishLanguagePref ? (
                        <ul>
                          {vasProd.termsOfSrvc.split('. ').map((term) => {
                            return term && <li key={term} dangerouslySetInnerHTML={{ __html: replaceURLWithHTMLLinks(term) }} />
                          })}
                        </ul>
                      ) : (
                        vasProductTermsSpanish.map((terms) => {
                          if (vasProd.serviceName === terms.heading) {
                            return (
                              <ul key={terms}>
                                {terms.serviceTerm.split('. ').map((t) => {
                                  return <li key={t} dangerouslySetInnerHTML={{ __html: replaceURLWithHTMLLinks(t) }} />
                                })}
                              </ul>
                            );
                          }
                        })
                      )}
                    </div>
                  </>
                ) : (
                  ''
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <h4 style={{ fontWeight: 'bold' }}>General</h4>
        {!spanishLanguagePref ? (
          generalTerms?.length > 0 ? (
            <>
              <ul className="terms-info-list ">
                {generalTerms[0]?.termsOfSrvc?.split('. ').map((i) => {
                  return i && <li key={i} dangerouslySetInnerHTML={{ __html: replaceURLWithHTMLLinks(i?.endsWith('.') ? i : i + '.') }}>
                  </li>
                })}
              </ul>
            </>
          ) : (
            <ul className="terms-info-list">
              {generalTermsStatic?.map((i) => (
                i && (
                  <li key={i} dangerouslySetInnerHTML={{ __html: i }}></li>
                )
              ))}
            </ul>
          )
        )
          : (
            <ul className="terms-info-list">
              {
                generalTermsSpanishArr?.length > 0 ?
                  generalTermsSpanishArr?.map((term, index) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: replaceURLWithHTMLLinks(term + '.') }} />
                  ))
                  :
                  spanishGeneralTermsStatic?.map((i) => (
                    i && (
                      <li key={i} dangerouslySetInnerHTML={{ __html: i }} />
                    )
                  ))
              }
            </ul>
          )}
      </div>
    </>
  );
};

const TermsInfo = () => {
  const scrollRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const thumbRef = React.useRef(null);
  const isDragging = React.useRef(false);
  const dragStart = React.useRef({ y: 0, scrollTop: 0 });
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const updateThumb = React.useCallback(() => {
    const el = scrollRef.current;
    const thumb = thumbRef.current;
    if (!el) {return;}
    const overflow = el.scrollHeight > el.clientHeight;
    setHasOverflow(overflow);
    if (!overflow || !thumb) {return;}
    const thumbHeight = Math.max((el.clientHeight / el.scrollHeight) * el.clientHeight, 30);
    const maxTop = el.clientHeight - thumbHeight;
    const thumbTop = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * maxTop;
    thumb.style.height = thumbHeight + 'px';
    thumb.style.transform = 'translateY(' + thumbTop + 'px)';
  }, []);

  React.useEffect(() => {
    updateThumb();
    window.addEventListener('resize', updateThumb);
    let observer;
    if (window.ResizeObserver) {
      observer = new ResizeObserver(updateThumb);
      // Observe the content element so reflows that change scrollHeight
      // (e.g. the English/Spanish toggle or async-loaded terms) trigger a recalc;
      // also observe the viewport for responsive size changes.
      if (contentRef.current) {observer.observe(contentRef.current);}
      if (scrollRef.current) {observer.observe(scrollRef.current);}
    }
    return () => {
      window.removeEventListener('resize', updateThumb);
      if (observer) {observer.disconnect();}
    };
  }, [updateThumb]);

  const handleThumbDragStart = React.useCallback((clientY) => {
    isDragging.current = true;
    dragStart.current = { y: clientY, scrollTop: scrollRef.current.scrollTop };
  }, []);

  const handleThumbMouseDown = React.useCallback((e) => {
    handleThumbDragStart(e.clientY);
    e.preventDefault();
  }, [handleThumbDragStart]);

  // Touch-only devices never fire mouse events, so dragging the custom thumb (the only way to
  // scroll on some touch browsers, since the hidden native scrollbar leaves no other drag target)
  // did nothing without these - the content itself still scrolls natively via overflow-y.
  const handleThumbTouchStart = React.useCallback((e) => {
    handleThumbDragStart(e.touches[0].clientY);
  }, [handleThumbDragStart]);

  React.useEffect(() => {
    const moveThumbTo = (clientY) => {
      const el = scrollRef.current;
      const thumb = thumbRef.current;
      if (!el || !thumb) {return;}
      const thumbH = parseFloat(thumb.style.height) || 30;
      const maxThumbTop = el.clientHeight - thumbH;
      const ratio = maxThumbTop > 0 ? (el.scrollHeight - el.clientHeight) / maxThumbTop : 0;
      el.scrollTop = dragStart.current.scrollTop + (clientY - dragStart.current.y) * ratio;
    };
    const handleMouseMove = (e) => {
      if (!isDragging.current) {return;}
      moveThumbTo(e.clientY);
    };
    const handleTouchMove = (e) => {
      if (!isDragging.current) {return;}
      moveThumbTo(e.touches[0].clientY);
    };
    const handleDragEnd = () => { isDragging.current = false; };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleDragEnd);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, []);

  const handleTrackClick = React.useCallback((e) => {
    if (isDragging.current) {return;}
    const el = scrollRef.current;
    if (!el) {return;}
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientY - rect.top) / rect.height;
    el.scrollTop = ratio * (el.scrollHeight - el.clientHeight);
  }, []);

  return (
    <div className="terms-description-scroll-wrapper">
      <div className="terms-description" ref={scrollRef} onScroll={updateThumb}>
        <div className="card-body" ref={contentRef}>
          <TermsContent />
        </div>
      </div>
      {hasOverflow && (
        <div className="custom-scrollbar-track" onClick={handleTrackClick}>
          <div
            className="custom-scrollbar-thumb"
            ref={thumbRef}
            onMouseDown={handleThumbMouseDown}
            onTouchStart={handleThumbTouchStart}
          />
        </div>
      )}
    </div>
  );
};

export default TermsInfo;
